import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, Clock, Layers, PenLine, MessageSquare, ShieldCheck,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getAuthenticityReport } from '../../lib/api';
import { studentLabel } from '../../lib/format';
import { BRAND } from '../../lib/brandColors';

/**
 * Authenticity report — process evidence for one draft (master spec Phase 6
 * item 5 / R8).
 *
 * This is deliberately NOT an AI detector and carries no verdict, no score,
 * and no accusation (North Star #3). It shows a teacher who is questioning a
 * submission the same thing a teacher walking the room would see: when the
 * student worked, how the draft grew, and what they asked the coach. Read
 * charitably, it is evidence FOR the student's process.
 *
 * There is no paste-event count. Nothing in the schema records paste events,
 * and an invented or permanently-zero number is exactly the kind of figure a
 * teacher would act on (master-spec hard constraint 11).
 */

const Tile = ({ icon: Icon, value, label, tone = 'text-teal-flow' }) => (
  <div className="card-surface p-4">
    <div className="flex items-center gap-3">
      <div className="p-2 bg-slate-700/60 rounded-lg">
        <Icon className={tone} size={18} />
      </div>
      <div>
        <p className="text-xl font-bold text-white">{value}</p>
        <p className="text-slate-400 text-xs">{label}</p>
      </div>
    </div>
  </div>
);

const formatWhen = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const formatDuration = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
};

/**
 * Working time is only measurable BETWEEN saves less than a session gap apart.
 * A student who saves once per sitting did real work DraftFlow never observed,
 * so zero measured minutes means "not measured", not "wrote it instantly".
 * Printing "under a minute" beside a 181-word draft would manufacture the exact
 * paste accusation this page exists to prevent.
 */
const sessionDuration = (session) => {
  if (session.minutes > 0) return formatDuration(session.minutes);
  return session.revisions > 1 ? 'Under a minute' : 'Single save';
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

export const AuthenticityReport = ({ draftId, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getAuthenticityReport(draftId);
        if (!cancelled) setReport(data);
      } catch (e) {
        console.error('Failed to load authenticity report:', e);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [draftId]);

  const header = (
    <div className="flex items-center gap-3">
      <button
        onClick={onClose}
        aria-label="Close writing process report"
        className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
      >
        <ChevronLeft size={20} />
      </button>
      <div>
        <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2">
          <ShieldCheck className="text-teal-flow" size={22} />
          How this draft was written
        </h2>
        {report && (
          <p className="text-slate-400 text-sm mt-0.5">
            {studentLabel(report)}
            {report.assignment_title ? ` · ${report.assignment_title}` : ''}
          </p>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 space-y-4" data-testid="authenticity-report">
        {header}
        <p className="text-slate-400 text-sm">Loading this draft's writing history…</p>
      </div>
    );
  }

  // `report.totals` is the shape guard, not just a null check: an unmatched
  // demo route resolves to `[]`, and any malformed payload must land on this
  // message rather than throwing inside the modal.
  if (failed || !report || !report.totals) {
    return (
      <div className="p-6 space-y-4" data-testid="authenticity-report">
        {header}
        <p className="text-slate-400 text-sm">
          Couldn't load this draft's writing history. Try again in a moment.
        </p>
      </div>
    );
  }

  const { totals, sessions, timeline, coach } = report;
  const chartData = (timeline || []).map((point, i) => ({
    step: i + 1,
    words: point.word_count,
    at: point.at,
  }));

  return (
    <div className="p-6 space-y-5" data-testid="authenticity-report">
      {header}

      <p className="text-slate-400 text-sm">
        A record of the writing process behind this draft: when {studentLabel(report)} worked,
        how the draft grew, and where the coach came in. It is evidence of process, not a score
        or a judgment.
      </p>

      {totals.revisions === 0 ? (
        <div className="card-surface p-6">
          <p className="text-slate-400 text-sm">
            No saved revisions were recorded for this draft yet, so there is no writing history
            to show. History starts building the first time the student writes in DraftSpace.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile
              icon={Clock}
              value={totals.writing_minutes > 0 ? formatDuration(totals.writing_minutes) : '—'}
              label="Time writing"
            />
            <Tile icon={Layers} value={totals.sessions} label={totals.sessions === 1 ? 'Writing session' : 'Writing sessions'} tone="text-flow-blue-hover" />
            <Tile icon={PenLine} value={totals.revisions} label="Saved revisions" tone="text-gold-bright" />
            <Tile icon={MessageSquare} value={coach.student_questions} label="Questions to the coach" tone="text-ai-accent" />
          </div>

          {totals.writing_minutes === 0 && (
            <p className="text-slate-500 text-xs">
              Time writing is measured between saves less than 15 minutes apart. This draft's
              saves are spread further apart, so the time spent writing wasn't measured. It is
              unknown, not brief. The timeline below shows when the work actually happened.
            </p>
          )}

          {/* Revision timeline — the draft's growth, save by save */}
          <div className="card-surface p-5">
            <h3 className="font-heading text-base font-semibold text-white mb-1">Revision timeline</h3>
            <p className="text-slate-400 text-xs mb-3">
              Word count at each save, from {formatWhen(totals.first_activity_at)} to {formatWhen(totals.last_activity_at)}.
            </p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                  <XAxis dataKey="step" tick={{ fill: '#64748b', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#fff' }}
                    labelFormatter={(step) => `Save ${step}`}
                    formatter={(value) => [`${value} words`, 'Length']}
                  />
                  <Line type="monotone" dataKey="words" stroke={BRAND.tealFlow} strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Writing sessions — same session rule as the live DraftBoard */}
          <div className="card-surface p-5">
            <h3 className="font-heading text-base font-semibold text-white mb-1">Writing sessions</h3>
            <p className="text-slate-400 text-xs mb-3">
              Each sitting the student spent on this draft. A gap longer than 15 minutes starts a new one.
            </p>
            <div className="space-y-2">
              {sessions.map((session, i) => (
                <div
                  key={session.started_at}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center gap-3">
                    <span className="status-chip status-chip--primary">Session {i + 1}</span>
                    <span className="text-slate-300 text-sm">{formatWhen(session.started_at)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs">
                    <span className="text-slate-400 flex items-center gap-1">
                      <Clock size={12} /> {sessionDuration(session)}
                    </span>
                    <span className={session.words_added < 0 ? 'text-gold-bright' : 'text-teal-flow'}>
                      {session.words_added >= 0 ? '+' : '-'}{plural(Math.abs(session.words_added), 'word')}
                    </span>
                    <span className="text-slate-500">{plural(session.revisions, 'save')}</span>
                    {session.coach_questions > 0 && (
                      <span className="text-ai-accent flex items-center gap-1">
                        <MessageSquare size={12} /> {session.coach_questions}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Coach conversation summary — the transcript itself lives on the draft card */}
      <div className="card-surface p-5">
        <h3 className="font-heading text-base font-semibold text-white mb-1">Coach conversation</h3>
        {coach.total_turns === 0 ? (
          <p className="text-slate-400 text-sm">
            This student didn't talk with the coach on this draft.
          </p>
        ) : (
          <p className="text-slate-400 text-sm">
            {studentLabel(report)} asked the coach {coach.student_questions}{' '}
            {coach.student_questions === 1 ? 'question' : 'questions'} and Drafty replied{' '}
            {coach.coach_replies} {coach.coach_replies === 1 ? 'time' : 'times'}, between{' '}
            {formatWhen(coach.first_at)} and {formatWhen(coach.last_at)}. Open "View coach
            transcript" on the draft to read every turn.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthenticityReport;
